/**
 * Umami Analytics Tracking Utility
 *
 * Lightweight wrapper around the Umami tracking script for:
 * - SPA route change tracking (page views)
 * - Custom event tracking (CTA clicks, form submissions, etc.)
 *
 * The Umami script is loaded via index.html. It exposes `window.umami`.
 * If the script hasn't loaded yet (or is blocked), calls are silently
 * no-ops so the app continues to work normally.
 */

declare global {
  interface Window {
    umami?: {
      trackView: (url: string, referrer?: string) => void;
      track: (eventName: string, data?: Record<string, unknown>) => void;
    };
  }
}

/**
 * Track a page view. Call this on every SPA route change.
 * Umami's script auto-tracks the initial page load, but for
 * client-side routing we need to manually track subsequent views.
 */
export function trackPageView(url: string): void {
  if (window.umami?.trackView) {
    window.umami.trackView(url);
  }
}

/**
 * Track a custom event with optional metadata.
 *
 * Examples:
 *   trackEvent('cta_click', { location: 'hero', label: 'book_demo' });
 *   trackEvent('form_submit', { form: 'contact', status: 'success' });
 *   trackEvent('nav_click', { target: 'about' });
 */
export function trackEvent(
  eventName: string,
  data?: Record<string, unknown>
): void {
  if (window.umami?.track) {
    window.umami.track(eventName, data);
  }
}

/**
 * Predefined event names for consistency across the app.
 */
export const AnalyticsEvents = {
  CTA_CLICK: 'cta_click',
  FORM_SUBMIT: 'form_submit',
  NAV_CLICK: 'nav_click',
  DEMO_REQUEST: 'demo_request',
  CONTACT_MESSAGE: 'contact_message',
  LANGUAGE_CHANGE: 'language_change',
  BLOG_VIEW: 'blog_view',
  SCROLL_DEPTH: 'scroll_depth',
} as const;
